import { Stack, Tooltip, Typography } from '@mui/material';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import { LoadedUser, Row } from '../../interfaces';
import {
  CampaignActionHandlers,
  MarketingCampaignWithSource,
} from '../../types/marketing';
import { HeaderCells } from '../../types/ui';
import DropdownMenu from '../../components/global/DropdownMenu';
import { pluralize } from '../global/helper';
import CampaignBadge from '../../components/marketing/CampaignBadge';
import {
  getAccessLevel,
  getCampaignActions,
  getCampaignLastEditedText,
  getCampaignMediumsText,
} from '../../helpers/marketing';
import CampaignAnalyticsCell from '../../components/marketing/CampaignsTable/CampaignAnalyticsCell';
import { getFullName } from '../../helpers/global';

export const headerCells = [
  { id: 'campaign', label: 'Campaign' },
  { id: 'status', label: 'Status' },
  { id: 'audience', label: 'Audience' },
  {
    id: 'analytics',
    label: 'Analytics',
    tooltip:
      'Email open rates may not be 100% accurate due to how email clients handle tracking. Some opens might not be counted, while others might be counted automatically by email systems.',
  },
  { id: 'action', label: '' },
] as const satisfies HeaderCells;

export function createRow(
  campaign: MarketingCampaignWithSource,
  {
    user,
    handlers,
    disableActions,
  }: {
    user: LoadedUser;
    handlers: CampaignActionHandlers;
    disableActions?: boolean;
  },
): Row {
  const { status, statistics } = campaign.source;
  const customersEnrolled = statistics.total_customers_enrolled || 0;
  const medium = getCampaignMediumsText(campaign.mediums, true);
  const accessLevel = getAccessLevel(user);
  const employee = campaign.employeeId
    ? user.business?.employees?.find(e => e.employeeId === campaign.employeeId)
    : null;
  const employeeName =
    employee?.displayName ||
    getFullName({
      firstName: employee?.user?.firstName,
      lastName: employee?.user?.lastName,
    });

  const options = getCampaignActions(campaign, user, handlers);
  const editedText =
    status === 'draft' ? getCampaignLastEditedText(campaign) : '';

  const row = {
    rowId: campaign.id,
    cells: [
      <Stack gap={0.25} maxWidth="400px" overflow="hidden">
        <Stack direction="row" gap={0.5} alignItems="center">
          <Typography variant="h5" noWrap>
            {campaign.name}
          </Typography>
          {campaign.level === 'PRIVATE' &&
            accessLevel !== 'self' &&
            !!employeeName && (
              <Tooltip
                title={`This campaign was created by ${employeeName}.`}
                placement="top"
                arrow
                slotProps={{
                  popper: {
                    modifiers: [
                      { name: 'offset', options: { offset: [0, -5] } },
                    ],
                  },
                }}>
                <PersonOutlinedIcon
                  color="primary"
                  sx={{ fontSize: 'body1.fontSize' }}
                />
              </Tooltip>
            )}
        </Stack>
        <Typography variant="body2">{medium}</Typography>
        {editedText && (
          <Typography
            variant="body2"
            sx={{
              color: 'text.disabled',
            }}>
            {editedText}
          </Typography>
        )}
      </Stack>,
      <CampaignBadge campaign={campaign} />,
      <Typography variant="body2">
        {status === 'draft'
          ? '-'
          : `${customersEnrolled} ${pluralize('Customer', customersEnrolled)}`}
      </Typography>,
      <CampaignAnalyticsCell campaign={campaign} />,
      options.length > 0 ? (
        <DropdownMenu options={options} disabled={disableActions} center />
      ) : null,
    ],
  };

  return row;
}
